/**
 * The choke point every value passes through before reaching CloudWatch RUM.
 * It is allowlist-first: only keys named in `rumEvents.ts` survive, and each
 * surviving value is scrubbed for accidental identifiers (UUID/ULID/email) and
 * URL/query content, length-capped, and reduced to a primitive. Anything that
 * can't be made safe is dropped, never partially passed through.
 *
 * Pure and AWS-free so it is fully unit-testable without aws-rum-web.
 */
import {
  EVENT_PROPERTY_KEY_SET,
  SESSION_ATTRIBUTE_KEY_SET,
  type SafePayload,
} from './rumEvents';

export const MAX_VALUE_LENGTH = 256;
export const MAX_PAYLOAD_KEYS = 32;
const REDACTED = '[redacted]';

// Allowlisted keys whose values are opaque, RANDOM identifiers by construction
// (no embedded PII) and are MEANT to be readable — the generic UUID/ULID scrub
// below would otherwise redact them and defeat their purpose. Only
// `supportCorrelationId` qualifies: it is minted from crypto.randomUUID() (see
// rumClient.ts::getSupportCorrelationId) purely to thread a front-end error to
// its backend log line. It carries no patient/clinic/entity id, so exempting it
// from id-redaction is safe; it is still length-capped and coerced to a
// primitive like every other value. Do NOT add a key here unless its value is
// provably random and PHI-free.
const OPAQUE_ID_KEYS: ReadonlySet<string> = new Set(['supportCorrelationId']);

// Allowlisted keys carrying a DELIBERATELY-PERMITTED identifier that must
// survive the UUID id-redaction below (a UUID-shaped user id / tenant id would
// otherwise be wiped): the logged-in user's OPAQUE Cognito id and their clinic
// id. Product decision (2026-07-22), stamped for any logged-in user (clinician
// or patient) so an error names which user + clinic without a lookup. These are
// LOW-identifying — an opaque id + a clinic, resolvable only internally, NOT
// plaintext PII. Email and full name are deliberately NOT here, and no patient
// clinical field ever is. Values are coerced to a non-empty string + length-capped.
const IDENTITY_KEYS: ReadonlySet<string> = new Set(['appUserId', 'clinicId']);

// Identifier shapes that must never ride inside an allowlisted value.
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
// Crockford base32 ULID (the system's stream/sub-session id shape).
const ULID_RE = /\b[0-9A-HJKMNP-TV-Z]{26}\b/;
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
// A URL or absolute/relative path — strip its query/hash, keep the path.
const URLISH_RE = /^(https?:\/\/[^/]+)?\/\S*$/i;

/**
 * Scrub a single string value:
 *  - if it carries a UUID/ULID/email anywhere → redact the whole value;
 *  - if it looks like a URL/path → drop the query + hash, keep the path;
 *  - always cap length.
 */
export function sanitizeStringValue(value: string): string {
  if (UUID_RE.test(value) || ULID_RE.test(value) || EMAIL_RE.test(value)) {
    return REDACTED;
  }
  let out = value;
  if (URLISH_RE.test(value)) {
    out = (value.split('?')[0] ?? '').split('#')[0] ?? '';
  }
  if (out.length > MAX_VALUE_LENGTH) {
    out = out.slice(0, MAX_VALUE_LENGTH);
  }
  return out;
}

/**
 * Coerce an arbitrary value to a RUM-safe primitive, or `undefined` to signal
 * "drop this". Objects/arrays/functions/symbols are dropped (no accidental
 * serialization of app state). NaN/Infinity are dropped.
 */
export function sanitizeValue(value: unknown): string | number | boolean | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  switch (typeof value) {
    case 'string': {
      const scrubbed = sanitizeStringValue(value);
      return scrubbed === '' ? undefined : scrubbed;
    }
    case 'number':
      return Number.isFinite(value) ? value : undefined;
    case 'boolean':
      return value;
    default:
      // objects, arrays, functions, symbols, bigint
      return undefined;
  }
}

/**
 * Value sanitizer for a key whose value must survive the id-redaction INTACT:
 * an OPAQUE_ID_KEYS field (a provably-random, PHI-free correlation id) or an
 * IDENTITY_KEYS field (a deliberately-permitted clinician/clinic identifier —
 * staff/customer-org, never patient). Returns a non-empty string, length-capped,
 * WITHOUT the UUID/ULID/email redaction. Non-strings/empties drop.
 */
export function sanitizeOpaqueId(value: unknown): string | undefined {
  if (typeof value !== 'string' || value === '') {
    return undefined;
  }
  return value.length > MAX_VALUE_LENGTH ? value.slice(0, MAX_VALUE_LENGTH) : value;
}

function sanitizeAgainstAllowlist(
  input: Record<string, unknown> | null | undefined,
  allowedKeys: ReadonlySet<string>,
): SafePayload {
  const out: SafePayload = {};
  if (!input || typeof input !== 'object') {
    return out;
  }
  let kept = 0;
  for (const key of Object.keys(input)) {
    if (kept >= MAX_PAYLOAD_KEYS) {
      break;
    }
    if (!allowedKeys.has(key)) {
      continue; // allowlist-first: silently drop everything else
    }
    const value =
      OPAQUE_ID_KEYS.has(key) || IDENTITY_KEYS.has(key)
        ? sanitizeOpaqueId(input[key])
        : sanitizeValue(input[key]);
    if (value === undefined) {
      continue; // drop undefined/null/unsafe
    }
    out[key] = value;
    kept += 1;
  }
  return out;
}

/** Filter + scrub an event payload down to the allowed property keys (C3). */
export function sanitizeEventPayload(
  payload: Record<string, unknown> | null | undefined,
): SafePayload {
  return sanitizeAgainstAllowlist(payload, EVENT_PROPERTY_KEY_SET);
}

/** Filter + scrub session attributes down to the allowed keys (C1). */
export function sanitizeSessionAttributes(
  attributes: Record<string, unknown> | null | undefined,
): SafePayload {
  return sanitizeAgainstAllowlist(attributes, SESSION_ATTRIBUTE_KEY_SET);
}

/**
 * Reduce an arbitrary thrown value to an Error that is safe to hand to
 * aws-rum-web's recordError. The raw `message` is dropped — it can be the
 * response interceptor's rewritten `title: details` backend string (PHI) — and
 * the stack's first line (which echoes that message) is replaced with just the
 * error name. The remaining stack frames (`at fn (file:line:col)`) are code
 * locations, carry no PHI, and still drive CloudWatch RUM source-map
 * unminification.
 *
 * Non-Error throws (strings/objects, e.g. `throw "<patient name>"`) are reduced
 * to a bare "NonError" — their content is NEVER stringified into telemetry.
 *
 * This is why aws-rum-web's built-in 'errors' telemetry is disabled: it would
 * capture the raw message/stack verbatim with no scrubbing hook. All error
 * capture goes through here instead.
 */
export function toSafeError(error: unknown): Error {
  if (!(error instanceof Error)) {
    const nonError = new Error('NonError');
    nonError.name = 'NonError';
    nonError.stack = 'NonError';
    return nonError;
  }
  const name = typeof error.name === 'string' && error.name.length > 0 ? error.name : 'Error';
  const safe = new Error(name); // message = name, never the raw message
  safe.name = name;
  if (typeof error.stack === 'string' && error.stack.length > 0) {
    // Drop the first line ("Name: <message>") which carries the raw message;
    // keep the frame lines (code locations only).
    const frames = error.stack.split('\n').slice(1);
    safe.stack = frames.length > 0 ? `${name}\n${frames.join('\n')}` : name;
  } else {
    safe.stack = name;
  }
  return safe;
}
